import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import * as api from './invitationInternalApi';
import {
  INVITATION_STATUS_BADGE,
  canConfirm,
} from './invitationInternalConstants';
import { formatDateTime } from '../formatters';

import InvitationTab from './components/InvitationTab';
import SupportingDocumentTab from './components/SupportingDocumentTab';
import ConfirmParticipationModal from './components/ConfirmParticipationModal';

// Invitation - Internal (Panitia Lelang) — Detail page.
//
// Reached from the list's View Detail action. Both statuses open this same page;
// status decides only whether the Confirm button is offered (Open) — nothing on
// the page is otherwise editable. Confirm -> popup (Present / Delegate) -> status
// becomes Confirmed and the participant is stored (Delegate only).
const TABS = [
  { id: 1, icon: 'ri-mail-open-line', label: 'INVITATION' },
  { id: 2, icon: 'ri-file-text-line', label: 'SUPPORTING DOCUMENT' },
];

const InvitationInternalDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [confirmModal, setConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const rec = await api.getInvitation(id);
      setDetail(rec);
      setNotFound(false);
    } catch (error) {
      if (String(error).includes('not found')) {
        setNotFound(true);
        setDetail(null);
      } else {
        toast.error(`Failed to load invitation: ${error}`);
      }
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  document.title = detail
    ? `Invitation - ${detail.procId}`
    : 'Invitation - Internal';

  const handleConfirm = async participation => {
    setSubmitting(true);
    try {
      await api.confirmInvitation(id, participation);
      setConfirmModal(false);
      toast.success('Attendance confirmed');
      await load();
    } catch (error) {
      toast.error(`Failed to confirm: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>
            <i className='ri-error-warning-line fs-1 text-muted'></i>
            <h5 className='mt-2'>Invitation not found</h5>
            <button
              className='btn btn-primary btn-sm mt-2'
              onClick={() => navigate('/crude/invitation-internal')}
            >
              Back to list
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5 text-muted'>Loading...</div>
        </Container>
      </div>
    );
  }

  const showConfirm = canConfirm(detail.invitationStatus);

  const infoCards = [
    { label: 'Procurement No', value: detail.procId, icon: 'ri-draft-line' },
    {
      label: 'Procurement Type',
      value: detail.procurementType,
      icon: 'ri-contrast-drop-2-line',
    },
    {
      label: 'Procurement Method',
      value: detail.procurementMethod,
      icon: 'ri-price-tag-2-line',
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title='Invitation Detail'
            pageTitle='Plan & Procurement'
          />

          {/* ---- Status strip + Confirm (top right, Open only) ---- */}
          <Row className='mb-3 align-items-center g-2'>
            <Col>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span className='text-muted fs-13'>
                  Invitation Internal Status:
                </span>
                <Badge
                  color={
                    INVITATION_STATUS_BADGE[detail.invitationStatus] ||
                    'secondary'
                  }
                  className='px-2 fs-12'
                >
                  {detail.invitationStatus}
                </Badge>
                {detail.confirmedAt && (
                  <span className='text-muted fs-12'>
                    confirmed {formatDateTime(detail.confirmedAt)}
                  </span>
                )}
              </div>
            </Col>
            {showConfirm && (
              <Col xs='auto'>
                <Button
                  color='success'
                  size='sm'
                  disabled={submitting}
                  onClick={() => setConfirmModal(true)}
                >
                  <i className='ri-check-double-line align-bottom me-1'></i>
                  Confirm
                </Button>
              </Col>
            )}
          </Row>

          {/* Once confirmed, show how the member is participating. */}
          {!showConfirm && detail.participant && (
            <div className='alert alert-light border d-flex align-items-start'>
              <i className='ri-information-line fs-16 me-2 mt-1'></i>
              <div>
                Attendance confirmed as{' '}
                <span className='fw-semibold'>{detail.participant.mode}</span>.
                {detail.participant.mode === 'Delegate' && (
                  <span>
                    {' '}
                    Delegate:{' '}
                    <span className='fw-semibold'>
                      {detail.participant.name}
                    </span>{' '}
                    ({detail.participant.email})
                  </span>
                )}
              </div>
            </div>
          )}

          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                {/* Info cards */}
                <Row className='mt-0 mb-3 g-3 d-flex align-items-stretch'>
                  {infoCards.map(c => (
                    <Col xl={4} md={6} className='mb-0' key={c.label}>
                      <div className='card bg-soft-primary border shadow-none h-100 mb-0'>
                        <div className='card-body py-3'>
                          <div className='d-flex'>
                            <div
                              className='d-flex me-3'
                              style={{ width: '100%' }}
                            >
                              <div className='flex-shrink-0 me-3'>
                                <i
                                  className={`${c.icon} fs-2 text-warning`}
                                ></i>
                              </div>
                              <div>
                                <h6 className='mb-1 fw-bold'>{c.label}</h6>
                                <h5 className='mb-0'>{c.value || '-'}</h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <Row className='justify-content-center mb-4'>
                  <Col xxl={12}>
                    <CardBody className='checkout-tab'>
                      <div className='mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-1 mb-3'
                        >
                          {TABS.map(tab => (
                            <NavItem key={tab.id}>
                              <NavLink
                                className={classnames('cursor-pointer', {
                                  active: activeTab === tab.id,
                                })}
                                onClick={() => setActiveTab(tab.id)}
                              >
                                <i
                                  className={`${tab.icon} align-middle me-1`}
                                ></i>
                                <span className='fw-semibold'>{tab.label}</span>
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={1}>
                          {activeTab === 1 && <InvitationTab detail={detail} />}
                        </TabPane>
                        <TabPane tabId={2}>
                          {activeTab === 2 && (
                            <SupportingDocumentTab
                              invitationId={id}
                              detail={detail}
                            />
                          )}
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>

        <ConfirmParticipationModal
          show={confirmModal}
          submitting={submitting}
          onConfirm={handleConfirm}
          onClose={() => setConfirmModal(false)}
        />
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </React.Fragment>
  );
};

export default InvitationInternalDetailView;
